/** Opaque media contract for anear-image-upload / anear-framed-image. */
import { normalizeOptionalImagePosition } from './imagePosition'

export function legacyFieldNamesFromName(nameAttr) {
  const name = String(nameAttr || '').trim()
  if (!name) {
    return {
      base: '',
      url: '',
      filename: '',
      position: '',
      media: ''
    }
  }
  const endsWithUrl = name.endsWith('Url')
  const base = endsWithUrl ? name.slice(0, -3) : name
  return {
    base,
    url: endsWithUrl ? name : `${base}Url`,
    filename: `${base}Filename`,
    position: `${base}Position`,
    media: name
  }
}

/**
 * Normalize to `{ url, filename, position }` or null when no usable url.
 * Accepts media object, JSON string, or a bare URL string.
 */
export function normalizeMedia(raw) {
  if (raw == null || raw === '') return null

  let parsed = raw
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed || trimmed === 'null') return null
    if (trimmed.startsWith('{')) {
      try {
        parsed = JSON.parse(trimmed)
      } catch {
        // Treat non-JSON strings as bare URLs
        parsed = { url: trimmed }
      }
    } else {
      parsed = { url: trimmed }
    }
  }

  if (typeof parsed !== 'object' || Array.isArray(parsed)) return null

  const url = String(parsed.url || parsed.src || '').trim()
  if (!url) return null

  const filenameRaw = parsed.filename
  const filename = filenameRaw == null || filenameRaw === ''
    ? null
    : String(filenameRaw).trim() || null

  return {
    url,
    filename,
    position: normalizeOptionalImagePosition(parsed.position)
  }
}

export function serializeMedia(media) {
  const normalized = normalizeMedia(media)
  if (!normalized) return ''
  return JSON.stringify({
    url: normalized.url,
    filename: normalized.filename,
    position: normalized.position
  })
}

/**
 * Build media from preferred opaque value, else split attrs / legacy pieces.
 */
export function assembleMedia({
  mediaValue,
  url,
  filename,
  position
} = {}) {
  const fromOpaque = normalizeMedia(mediaValue)
  if (fromOpaque) return fromOpaque

  const urlText = String(url || '').trim()
  if (!urlText) return null

  // If "url" is itself opaque JSON, prefer that
  const urlAsMedia = normalizeMedia(urlText)
  if (urlAsMedia && urlText.startsWith('{')) return urlAsMedia

  return normalizeMedia({
    url: urlText,
    filename: filename || null,
    position: position ?? null
  })
}
